/**
 * Phase 6e2c-1 — SPECIAL OPTIONS selector (§33.10).
 *
 * React port of components/special_options/view/special_options.selector.js
 * (271) + its tmpl. AHU-level (no canvas, no rate/print). Catalog GET
 * settings_ordered + AHU rows GET ahu/special_options/ (both read-only, fired on
 * mount); a category-grouped checkbox table + qty inputs + a "Selected (N)" tab
 * + a show-only-selected toggle + ellipsis tooltips; a `changes{}` diff that, on
 * the user's explicit Select click ONLY, destroys unchecked existing rows /
 * PATCHes qty updates / creates adds — then cancels.
 *
 * DATA-SAFETY (§4 + the 6e2a userArmed gate): the two GETs are read-only; the
 * three mutations are wired to the chassis `select` fan-out event, which the
 * chassis fires only from a real #select button click. Mounting, browsing,
 * toggling checkboxes and editing qty stay entirely local/in-memory (React
 * state) until Select. ZERO mutating calls on open/browse/toggle/cancel.
 *
 * Legacy match-key note: the build-match (`resalt.special_option_id === opt.id`)
 * and the changes key both reduce to the master special-option id; in the live
 * dev3 catalog `cms_s_o_s.id === special_option_name_id` for every checkbox row,
 * so matching on `special_option_id` is byte-equivalent to the legacy result and
 * is the semantically correct key (AHU row.special_option_id ↔ catalog
 * row.special_option_id). The create payload sends `special_option_id` (master).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import type { SelectorTabProps } from './selectorTypes';
import {
  useGetSpecialOptionsCatalogQuery,
  useGetAhuSpecialOptionsQuery,
  useCreateAhuSpecialOptionMutation,
  usePatchAhuSpecialOptionMutation,
  useDeleteAhuSpecialOptionMutation,
  type ISpecialOptionCatalogRow,
  type IAhuSpecialOptionRow,
} from './specialOptionsApi';

/** A catalog row enriched with the AHU's saved selection (initial state). */
interface OptionRow {
  /** master special-option id (the checkbox/qty/create key + match key). */
  specialOptionId: number;
  label: string;
  description: string;
  mandatory: boolean;
  /** The class no longer offers this option; it is listed only because this
   *  AHU already selected it before the admin withdrew it. Grandfathered: it
   *  stays selected and keeps costing what it costs until the USER unchecks
   *  it, at which point it leaves the list for good. */
  withdrawn: boolean;
  /** the ahu/special_options row id when this option is already saved. */
  ahuRowId: number | null;
  initialChecked: boolean;
  initialQty: number;
}

interface CategoryGroup {
  name: string;
  /** stable tab id (`${name}_` — byte-identical to the legacy data-name). */
  tabId: string;
  rows: OptionRow[];
}

/** Live (in-memory) edit state per option — keyed by specialOptionId. */
interface EditState {
  checked: boolean;
  qty: number;
}

const SELECTED_TAB = 'selected';

export const SpecialOptionsSelector = ({
  modelId,
  ahu: ahuProp,
  setSelectEnabled,
}: SelectorTabProps) => {
  // `SelectorTabProps.ahu` became OPTIONAL in Phase 6.5 for the header
  // "independent rating" host — but special_options is BOARD-ONLY: the header
  // chassis's `show_tab:'all'` branch does not emit a special_options tab at all
  // (component_selector.tmpl:4-11), so this selector always has the board AHU.
  const ahu = ahuProp as { id: number } & Record<string, unknown>;

  /**
   * ★ A STATIC VERSION OPENS READ-ONLY. It is a record of what was quoted, so
   * the dialog is worth reading -- which category an option sat in, what was
   * selected and at what quantity -- but nothing in it may be changed. The
   * board's CSS disables every other rating dialog outright on a static
   * version; the Special Options trigger overrides that inline (see
   * `TabAhu`) and the dialog locks itself instead, so the tabs still
   * navigate.
   *
   * Belt and braces, not decoration: the server refuses the writes anyway
   * (`check_access_to_the_ahu` raises "Static version"), so without this the
   * user could tick a box, press Select and get a silent failure.
   */
  const readOnly = Boolean(ahu.static_version);
  const catalogQuery = useGetSpecialOptionsCatalogQuery(ahu.id);
  const rowsQuery = useGetAhuSpecialOptionsQuery(ahu.id);

  const [createOption] = useCreateAhuSpecialOptionMutation();
  const [patchOption] = usePatchAhuSpecialOptionMutation();
  const [deleteOption] = useDeleteAhuSpecialOptionMutation();

  const loading = catalogQuery.isLoading || rowsQuery.isLoading;
  const catalog = catalogQuery.data;
  const ahuRows = rowsQuery.data;

  // ---- buildSpecialOptionsByCategory (§33.10) ----
  const groups = useMemo<CategoryGroup[]>(() => {
    if (!catalog) return [];
    const rows = ahuRows ?? [];
    const byCategory = new Map<string, OptionRow[]>();
    catalog.forEach((opt: ISpecialOptionCatalogRow) => {
      const category = opt.special_option_category_name || 'None';
      const saved = rows.find(
        (r: IAhuSpecialOptionRow) => r.special_option_id === opt.special_option_id
      );
      const row: OptionRow = {
        specialOptionId: opt.special_option_id,
        label: opt.special_option_label ?? opt.special_option_name ?? '',
        description: opt.special_option_description ?? '',
        mandatory: Boolean(opt.mandatory),
        withdrawn: opt.checkbox === false,
        ahuRowId: saved ? saved.id : null,
        initialChecked: Boolean(saved) || Boolean(opt.mandatory),
        initialQty: saved && saved.qty != null ? saved.qty : 1,
      };
      const list = byCategory.get(category) ?? [];
      list.push(row);
      byCategory.set(category, list);
    });
    // tabId mirrors the legacy `data-name="{{name}}_{{id}}"` — and the legacy
    // category object has no `id`, so every data-name is `${name}_` with a bare
    // trailing underscore. That exact string is load-bearing: common.css:25982
    // (`rating_mode_special_options.scss:50`) is `li[data-name="None_"] {order:1}`,
    // which flex-orders the uncategorised tab past the bottom-pinned Selected tab.
    // An index suffix produced `None_3` and silently missed that selector.
    // Category names are Map keys, so `${name}_` is still unique.
    return Array.from(byCategory.entries()).map(([name, list]) => ({
      name,
      tabId: `${name}_`,
      rows: list,
    }));
  }, [catalog, ahuRows]);

  /**
   * Withdrawn options the user has just unchecked (see `toggleOption`). They
   * leave the list immediately and cannot be re-checked -- the class does not
   * offer them, and the selection that earned them their place is gone.
   */
  const [dismissed, setDismissed] = useState<Set<number>>(() => new Set());

  /**
   * What the dialog actually renders: every group minus the rows dismissed in
   * this session, minus any category that empties out as a result. The
   * SEEDING and the Select diff below deliberately keep reading `groups`, not
   * this -- a dismissed row still has to be sent as a DELETE.
   */
  const visibleGroups = useMemo<CategoryGroup[]>(() => {
    if (dismissed.size === 0) return groups;
    return groups
      .map((g) => ({ ...g, rows: g.rows.filter((r) => !dismissed.has(r.specialOptionId)) }))
      .filter((g) => g.rows.length > 0);
  }, [groups, dismissed]);

  // ---- live edit state (local until Select; the legacy `changes{}` is derived) ----
  const [edits, setEdits] = useState<Record<number, EditState>>({});
  // Seed from the AHU's saved rows once the data resolves (re-seeds if data id changes).
  // `seededFor` is STATE, not a ref, on purpose: the collapse effect below reads it.
  // A ref mutated here reads as "already seeded" in the very same commit — while
  // `edits` is still the pre-seed `{}` and `selectedCount` is therefore 0 — which
  // collapsed the Selected tab to the first category on every open. As state it
  // flips in the same batch as `setEdits`, so the two are never observed out of step.
  const [seededFor, setSeededFor] = useState<number | null>(null);
  useEffect(() => {
    if (loading || !catalog) return;
    if (seededFor === ahu.id) return;
    const seed: Record<number, EditState> = {};
    groups.forEach((g) =>
      g.rows.forEach((r) => {
        seed[r.specialOptionId] = { checked: r.initialChecked, qty: r.initialQty };
      })
    );
    setEdits(seed);
    setSeededFor(ahu.id);
  }, [loading, catalog, groups, ahu.id, seededFor]);

  // ---- active tab (category tabId | 'selected') ----
  // Legacy default: the Selected tab is the default-rendered active one (tmpl
  // marks `.selected-nav-item.selected`); `updateSelectedOptionsCount` only
  // collapses it to the first category when `checked < 1`. So the initial tab is
  // SELECTED_TAB and the collapse below is the ONLY thing that moves it off —
  // driven by the SEEDED `selectedCount`, never a stale pre-seed read.
  const [activeTab, setActiveTab] = useState<string>(SELECTED_TAB);
  const firstTabId = visibleGroups[0]?.tabId;

  const selectedCount = useMemo(
    () => Object.values(edits).filter((e) => e.checked).length,
    [edits]
  );

  // updateSelectedOptionsCount: when the count is 0 while the Selected tab is
  // active, fall back to the first category (legacy clicks nth-child(1)). Gated
  // on seeding (seededFor) so the pre-seed `{}` state can't prematurely collapse
  // an AHU that actually has saved/mandatory selections.
  useEffect(() => {
    if (
      seededFor === ahu.id &&
      selectedCount < 1 &&
      activeTab === SELECTED_TAB &&
      firstTabId
    ) {
      setActiveTab(firstTabId);
    }
  }, [seededFor, selectedCount, activeTab, firstTabId, ahu.id]);

  // ---- #select availability: enabled once the user touches anything (legacy
  // removes #select.non-clickable on the FIRST change). ----
  const dirty = useRef(false);
  const markDirty = () => {
    if (readOnly) return;            // nothing to commit, so Select stays off
    if (!dirty.current) {
      dirty.current = true;
      setSelectEnabled(true);
      ahuBoardBus.emit('rating_mode:mounting:changed'); // chassis makeChanges tick
    }
  };

  /**
   * Options the admin has withdrawn from the class, which the user has now
   * unchecked. They vanish from the list the moment they are unchecked and
   * cannot be re-checked: the class does not offer them any more, so the only
   * reason they were listed at all was the existing selection the user has
   * just given up. (After Select they are gone server-side too, and the next
   * open will not list them.)
   */
  const toggleOption = (row: OptionRow) => {
    if (readOnly) return;
    if (row.mandatory) return; // mandatory checkbox is disabled (non-clickable)
    setEdits((prev) => {
      const cur = prev[row.specialOptionId] ?? { checked: false, qty: 1 };
      const checked = !cur.checked;
      return {
        ...prev,
        // checking resets qty to 1 (legacy qty_input.val(1) on checkbox change).
        [row.specialOptionId]: { checked, qty: checked ? 1 : cur.qty },
      };
    });
    if (row.withdrawn && edits[row.specialOptionId]?.checked) {
      setDismissed((prev) => new Set(prev).add(row.specialOptionId));
    }
    markDirty();
  };

  const changeQty = (row: OptionRow, raw: string) => {
    if (readOnly) return;
    const qty = Math.max(1, Number(raw.replace(/,/g, '')) || 1);
    setEdits((prev) => ({
      ...prev,
      [row.specialOptionId]: {
        checked: prev[row.specialOptionId]?.checked ?? true,
        qty,
      },
    }));
    markDirty();
  };

  // ---- table-wrapper ref (drives ellipsis tooltips + scrollbar marker) ----
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // ---- ellipsis tooltips (isTextEllipsed) ----
  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return;
    root.querySelectorAll<HTMLElement>('tbody.active td.description').forEach((el) => {
      const ellipsed =
        el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
      const tooltip = el.querySelector<HTMLElement>('.description-tooltip');
      if (ellipsed) {
        el.classList.add('ellipsed');
        if (tooltip) tooltip.style.width = `${el.clientWidth - 50}px`;
      } else {
        el.classList.remove('ellipsed');
        if (tooltip) tooltip.style.display = 'none';
      }
    });
  }, [activeTab, visibleGroups, edits]);

  // ---- vertical-scrollbar marker (hasVerticalScrollbar) ----
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    el.classList.toggle('with-scroll', el.scrollHeight > el.clientHeight);
  }, [activeTab, visibleGroups]);

  // ---- Select CRUD (§33.10) — fired ONLY from the chassis select fan-out
  // (a real #select click). Mirrors the legacy select(): destroy / patch / create. ----
  useEffect(() => {
    const selectKey =
      `special_options_${modelId}:selector:select` as `${string}_${number}:selector:select`;
    const off = ahuBoardBus.on(
      selectKey,
      () => {
        const adds: Array<{ specialOptionId: number; qty: number }> = [];
        const updates: Array<{ rowId: number; qty: number }> = [];
        const deletes: number[] = [];
        groups.forEach((g) =>
          g.rows.forEach((r) => {
            const e = edits[r.specialOptionId];
            if (!e) return;
            if (r.ahuRowId != null) {
              // existing row
              if (e.checked) updates.push({ rowId: r.ahuRowId, qty: e.qty });
              else deletes.push(r.ahuRowId);
            } else if (e.checked) {
              adds.push({ specialOptionId: r.specialOptionId, qty: e.qty });
            }
          })
        );
        deletes.forEach((rowId) => {
          void deleteOption({ rowId, ahuUnitId: ahu.id });
        });
        updates.forEach(({ rowId, qty }) => {
          void patchOption({ rowId, ahuUnitId: ahu.id, qty });
        });
        adds.forEach(({ specialOptionId, qty }) => {
          void createOption({ ahuUnitId: ahu.id, specialOptionId, qty });
        });
        // select() ends with cancelChanges(true) → rating_mode:hide.
        ahuBoardBus.emit('rating_mode:hide');
      }
    );
    return off;
  }, [modelId, groups, edits, ahu.id, createOption, patchOption, deleteOption]);

  if (loading) {
    return <div className="preloader dotted-preloader for-empty-list active" />;
  }

  const selectedTabActive = activeTab === SELECTED_TAB;

  return (
    <div
      id="special_options_new"
      className="special_options input-part coil-new row special_options-rating-mode"
    >
      <div className="child-1 nav nav-tabs">
        <div>
          <ul className="col-xs-12">
            {visibleGroups.map((g) => (
              <li
                key={g.tabId}
                className={`input-nav-item${activeTab === g.tabId ? ' selected' : ''}`}
                data-name={g.tabId}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab(g.tabId);
                }}
              >
                <a data-toggle="tab" href={`#${g.name}`}>
                  {g.name}
                </a>
              </li>
            ))}
            <li
              className={`input-nav-item selected-nav-item${
                activeTab === SELECTED_TAB ? ' selected' : ''
              }${selectedCount < 1 ? ' non-clickable' : ''}`}
              data-name={SELECTED_TAB}
              onClick={(e) => {
                e.preventDefault();
                if (selectedCount < 1) return;
                setActiveTab(SELECTED_TAB);
              }}
            >
              <a data-toggle="tab" href="#selected">
                Selected ({selectedCount})
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="child-2 content-data tab-content">
        <div ref={wrapperRef} className="table-wrapper">
          <table>
            <thead>
              <tr className="special-options-labels special-options-row">
                <td>SPECIAL OPTION</td>
                <td>DESCRIPTION</td>
                <td>QUANTITY</td>
              </tr>
            </thead>

            {/* One tbody per category (legacy renders all; the active one is
                shown via .active, and the "Selected" tab adds .only-selected to
                EVERY tbody so all categories' checked rows show across the table
                — see special_options.selector.js activateInputLabel). */}
            {visibleGroups.map((g) => {
              const isActiveBody = !selectedTabActive && g.tabId === activeTab;
              return (
                <tbody
                  key={g.tabId}
                  className={`${isActiveBody ? 'active' : ''}${
                    selectedTabActive ? ' only-selected' : ''
                  }`.trim()}
                  data-toggle={g.tabId}
                >
                  {g.rows.map((row) => {
                    const e = edits[row.specialOptionId] ?? {
                      checked: row.initialChecked,
                      qty: row.initialQty,
                    };
                    return (
                      <tr
                        key={row.specialOptionId}
                        /* `withdrawn-option` marks a row the class no longer
                           offers, listed only because this AHU still has it
                           selected. ★ DELIBERATELY UNSTYLED: product asked
                           for a withdrawn option to look like any other
                           (2026-10-21), so this is a hook for later, not a
                           dangling style. The behaviour is in `toggleOption`:
                           unchecking one removes it for good. */
                        className={`special-options-row ${
                          row.mandatory ? 'none-' : ''
                        }selectable${e.checked ? '' : ' unselected-row'}${
                          row.withdrawn ? ' withdrawn-option' : ''
                        }`}
                        onClick={(ev) => {
                          if ((ev.target as HTMLElement).tagName === 'INPUT') return;
                          toggleOption(row);
                        }}
                      >
                        <td>
                          <input
                            className={`option-field${
                              row.mandatory ? ' non-clickable' : ''
                            }`}
                            type="checkbox"
                            checked={e.checked}
                            data-id={row.specialOptionId}
                            disabled={readOnly || row.mandatory}
                            readOnly={readOnly || row.mandatory}
                            onChange={() => toggleOption(row)}
                          />
                          <span>{row.label}</span>
                        </td>
                        <td className="description">
                          {row.description}
                          <div className="description-tooltip">{row.description}</div>
                        </td>
                        <td>
                          <input
                            className="option-field"
                            value={e.qty}
                            disabled={readOnly || !e.checked}
                            data-id={row.specialOptionId}
                            type="number"
                            min={1}
                            step={1}
                            onChange={(ev) => changeQty(row, ev.target.value)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              );
            })}
          </table>
        </div>
      </div>
    </div>
  );
};
